import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  createMcpApiKeyApi,
  deleteMcpApiKeyApi,
  listMcpApiKeysApi,
} from "../../../services/api/mcpApi";
import {
  MCP_TEXT,
  mcpErrorMessage,
  normalizeApiKey,
  normalizeCreatedKey,
  normalizeKeyList,
} from "../../HelperFunction/mcpHelpers";

export default function useMcpApiKeys({ userId }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setError(MCP_TEXT.USER_MISSING);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await listMcpApiKeysApi(userId);
      setKeys(normalizeKeyList(response.data).map((raw) => normalizeApiKey(raw)));
    } catch (err) {
      if (err?.response?.status === 404) {
        setKeys([]);
      } else {
        console.error("MCP: could not load API keys", err);
        setError(mcpErrorMessage(err, MCP_TEXT.LOAD_ERROR));
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const createKey = useCallback(
    async (payload) => {
      setSaving(true);

      try {
        const response = await createMcpApiKeyApi(userId, payload);
        toast.success(MCP_TEXT.CREATE_SUCCESS);
        await load();
        return normalizeCreatedKey(response.data);
      } catch (err) {
        toast.error(mcpErrorMessage(err, MCP_TEXT.CREATE_FAILED));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [userId, load],
  );

  const deleteKey = useCallback(
    async (keyId) => {
      setSaving(true);

      try {
        await deleteMcpApiKeyApi(userId, keyId);
        toast.success(MCP_TEXT.DELETE_SUCCESS);
        await load();
        return true;
      } catch (err) {
        toast.error(mcpErrorMessage(err, MCP_TEXT.DELETE_FAILED));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [userId, load],
  );

  return { keys, loading, error, saving, reload: load, createKey, deleteKey };
}
